import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from '@/components/ContentLayout'
import type { ReactNode } from 'react'
import { TabBar, TabItem } from '@/components/TabBar'
import { useMatch } from 'react-router'
import { links } from '@/utils/link'
import { byDesign } from '@/infra/design'
import imgWorkspaceBillingUrl from '@/assets/misc/img_workspace-billing.svg'
import WorkspaceBillingIllustration from '@/components/SpotIllustration/WorkspaceBillingIllustration'
import imgPaymentMethodsUrl from '@/assets/misc/img_payment-methods.svg'
import NoPaymentMethodsIllustration from '@/components/SpotIllustration/NoPaymentMethodsIllustration'

export interface BillingProps {
  header: ReactNode
  /**
   * The billing routes, rendering `PaymentMethodsSection` and
   * `PayoutMethodSection`.
   */
  children?: ReactNode
  stripeConnectPayoutModal?: ReactNode
}

export function Billing({
  header,
  children,
  stripeConnectPayoutModal,
}: BillingProps) {
  return (
    <div>
      {header}
      <ContentLayout accountForTabBar accountForLabel>
        {children}
      </ContentLayout>

      {stripeConnectPayoutModal}
    </div>
  )
}

export interface BillingTabBarProps {
  isPayoutsEnabled: boolean
}

export function BillingTabBar({ isPayoutsEnabled }: BillingTabBarProps) {
  return (
    <TabBar align="left">
      <BillingTabItem to={links.profileBillingPaymentMethods()}>
        Payment Methods
      </BillingTabItem>

      {isPayoutsEnabled && (
        <BillingTabItem to={links.profileBillingPayoutMethod()}>
          Payout Method
        </BillingTabItem>
      )}
    </TabBar>
  )
}

export interface PaymentMethodsSectionProps {
  corporateMode: boolean
  children?: ReactNode
}

export function PaymentMethodsSection({
  corporateMode,
  children,
}: PaymentMethodsSectionProps) {
  return (
    <>
      <ContentMain>{children}</ContentMain>
      <ContentTip
        corporateMode={corporateMode}
        illustrationUrl={imgWorkspaceBillingUrl}
        illustration={byDesign(undefined, WorkspaceBillingIllustration)}
        title="Transactions don't have to be complicated"
      >
        Are you looking to get more out of your Workspace? Our in-depth Help
        Center can help!
      </ContentTip>
    </>
  )
}

export interface PayoutMethodSectionProps {
  corporateMode: boolean
  children?: ReactNode
}

export function PayoutMethodSection({
  corporateMode,
  children,
}: PayoutMethodSectionProps) {
  return (
    <>
      <ContentMain>{children}</ContentMain>

      <ContentTip
        corporateMode={corporateMode}
        illustrationUrl={imgPaymentMethodsUrl}
        illustration={byDesign(undefined, NoPaymentMethodsIllustration)}
        title="Review your payout method"
      >
        We’ve made some changes to our payout system and only allow one payout
        method per user.
      </ContentTip>
    </>
  )
}

interface BillingTabItemProps {
  to: string
  children?: ReactNode
}

function BillingTabItem({ to, children }: BillingTabItemProps) {
  return (
    <TabItem active={Boolean(useMatch(to))} to={to}>
      {children}
    </TabItem>
  )
}
